import {
  ref,
  Ref,
  computed,
  EffectScope,
  effectScope,
  getCurrentScope,
  watch,
  onUnmounted
} from 'vue';
import axios from 'axios';
import { IdLabel, IdLabelList, PlainObject } from './types';
import { formatDecimal, assertRestResponse, computedAsyncEx } from './Common';
import { ICellRendererParams } from 'ag-grid-community';
import _ from 'lodash-es';

// DataSource
export interface IdLabelEx extends IdLabel {
  raw?: any; // 保存接口的原始数据, 提供id, label之外的数据, 通过“transformProps”函数获取
}

export type IdLabelExList = IdLabelEx[];

type DataSourceState = 'init' | 'running' | 'ok' | 'fail';
// DataSource 结构
export type TransformDataSource<T = IdLabelExList> = {
  type: 'TransformDataSource';
  data: Ref<T>;
  state: Ref<DataSourceState>;
  load: (reload?: boolean) => void;
  scope: EffectScope; // reactive scope
  global: boolean;
};

function isTransformDataSource(data: any): data is TransformDataSource<any> {
  return data.type == 'TransformDataSource';
}

type TransformProvider<T = IdLabelExList> = (
  abortController: AbortController
) => Promise<T>;

// global transform scope
const globalScope = effectScope(true);

// 回调函数转成datasource
export function transformProviderToDataSource<T = IdLabelExList>(
  provider: TransformProvider<T>,
  options?: { global?: boolean; lazy?: boolean; logInfo?: string }
): TransformDataSource<T> {
  const global = !!options?.global;
  const lazy = options?.lazy;
  //const logInfo = options?.logInfo;

  // 选择reactive watch作用域, globalScope在组件unmount后仍保持有效
  const scope = global ? globalScope : getCurrentScope()!;
  const evaluating = ref(false);

  const { data, state, load } = scope.run(() => {
    const { data, state, cancel, load } = computedAsyncEx(provider, undefined, {
      lazy,
      evaluating
      //logInfo
    });

    /*// 增加loading状态
    const nstate = computed(() => {
      if (evaluating.value) {
        return 'loading';
      }
      return state.value;
    });*/

    return {
      data,
      state,
      cancel,
      load
    };
  })!;

  return {
    type: 'TransformDataSource',
    data,
    state,
    load,
    scope,
    global
  };
}

// 数据源转换
export function convertTransformDataSource<T = IdLabelExList, S = any>(
  source: TransformDataSource<S>,
  converter: (s: S) => T
): TransformDataSource<T> {
  const { data, state, load, scope, global } = source;

  const ndata = scope.run(() => {
    return computed(() => converter(data.value));
  })!;

  return {
    type: 'TransformDataSource',
    data: ndata,
    state,
    load,
    scope,
    global
  };
}

///////////////////////////////////////////////////////////////////
// 变换
interface Refresher {
  agApi: any;
  colId: string;
}

interface Transform {
  name: string; // 变换名
  map: (i: number | string, refresher?: Refresher) => string | undefined;
  success: boolean; // 加载成功过
  dataMap: Record<number | string, string>; // id label map
  dataRef: Ref<IdLabelList>; // id label列表
  dataProps: Ref<Record<number | string, PlainObject>>; // 全部属性
  global: boolean; // 是全局, 还是局部(自动注销)
  rawId: boolean; // 保持原始id, 即不转成number
  localRefCount: number; // 局部组件的引用计数
  pendings: Refresher[];

  datasource: TransformDataSource;
  destory: () => void; // 资源释放
}

const transforms: Record<string, Transform> = {};

function registerStaticTransform(
  name: string,
  transform: { [K: number]: string },
  global: boolean
) {
  const list = [] as IdLabelList;

  for (const [idKey, label] of Object.entries(transform)) {
    const id = parseInt(idKey);
    list.push({ id, label });
  }

  const datasource: TransformDataSource = {
    type: 'TransformDataSource',
    data: ref(list),
    state: ref('ok'),
    load: _.noop,
    // 构造一个假的scope
    scope: {
      detached: false,
      run(fn: () => any) {
        return fn();
      },
      stop: _.noop
    } as EffectScope,
    global
  };

  const t: Transform = {
    name,
    map: ((i: number) => transform[i]) as any,
    dataMap: transform,
    dataRef: datasource.data,
    dataProps: ref({}),
    success: true,
    global,
    rawId: false,
    localRefCount: 0,
    pendings: [],
    destory: _.noop,
    datasource
  };

  transforms[name] = t;
  return t;
}

/**
 * 需要在scope.run中调用_registerApiTransform
 * @param name 变换名
 * @param datasource 数据源
 * @param global 是否全局transform(局部的transform在组件unmounted时会销毁)
 */
function registerApiTransform(
  name: string,
  datasource: TransformDataSource,
  global: boolean,
  rawId: boolean
) {
  let pendings = [] as Refresher[];

  // 之前的pending要保留下来
  if (transforms[name]) {
    pendings = transforms[name].pendings!;
  }

  // 全部属性
  const dataPropsRef = ref({} as Record<number | string, PlainObject>);

  // map数据
  const dataRef = ref([] as IdLabelList);

  const dataSourceState = datasource.state;
  const dataSourceData = datasource.data;

  // 重新加载标记 isReload
  let isReload = false;
  const unwatch = datasource.scope.run(() => {
    return watch(
      dataSourceState,
      (state) => {
        if (state == 'fail') {
          pendings.splice(0);
          console.error(`Transform load fail: ${name}`);
          return;
        }

        if (state != 'ok') {
          return;
        }

        // 填充数据
        const dataMap = {} as Record<number | string, string>;
        const idList: (number | string)[] = [];

        for (const item of dataSourceData.value) {
          const id = rawId ? item.id : _.toInteger(item.id);
          idList.push(id);

          dataMap[id] = item.label;

          // 重新加载
          if (isReload) {
            // 删除已存在的
            _.remove(dataRef.value, (e) => e.id == id);
          }

          dataRef.value.push({
            id,
            label: item.label,
            disabled: item.disabled
          });

          dataPropsRef.value[id] = item.raw ?? item;
        }

        // 重新加载
        if (isReload) {
          _.remove(dataRef.value, (e) => !_.includes(idList, e.id));

          for (const key of Object.keys(dataPropsRef.value)) {
            const id = rawId ? key : _.toInteger(key);
            if (!_.includes(idList, id)) {
              Reflect.deleteProperty(dataPropsRef.value, id);
            }
          }
        }

        // 数据加载成功, 覆盖fun
        transform.dataMap = dataMap;
        transform.map = (i: number | string) => dataMap[i];
        transform.success = true;

        // 刷新表格未获得数据的列
        for (const p of pendings) {
          try {
            p.agApi.refreshCells({
              force: true,
              columns: [p.colId]
            });
          } catch (e) {}
        }

        pendings.splice(0);

        // 标记加载成功过
        isReload = true;
      },
      { immediate: true }
    );
  })!;

  const transform: Transform = {
    name,
    //api,
    map: (_i: number | string, refresher?: Refresher): string | undefined => {
      // 执行加载操作
      const state = datasource.state.value;
      if (state == 'init' || state == 'fail') {
        datasource.load();
      }

      if (refresher) {
        // pendings去掉重复
        if (
          !pendings.find(
            (e) => refresher.agApi == e.agApi && refresher.colId == e.colId
          )
        ) {
          pendings.push(refresher);
        }
      }

      return undefined;
    },
    pendings: pendings,
    dataMap: {},
    dataRef,
    dataProps: dataPropsRef,
    success: false,
    global: global,
    rawId,
    localRefCount: 0,
    destory: () => {
      unwatch();
      transform.destory = _.noop;
    },
    datasource
  };

  transforms[name] = transform;

  return transform;
}

function axiosTransformProvider(
  name: string,
  url: string,
  options: {
    id?: string;
    label?: string;
    disabled?: string; // disabled 字段名
    method?: string;
    body?: PlainObject;
    exclude?: (item: IdLabel, raw: PlainObject) => boolean;
  }
): TransformProvider {
  return (abortController: AbortController) => {
    // 调用axios
    const axiosResponse =
      options.method == 'get'
        ? axios.get(url, {
            signal: abortController.signal
          })
        : axios.post(url, options.body ?? {}, {
            signal: abortController.signal
          });

    const response = axiosResponse.then((resp) => {
      const data = (assertRestResponse(resp) as any) || [];

      const idLabelList: IdLabelExList = [];
      for (const row of data) {
        const rowId = row[options.id!];
        if (rowId === undefined) {
          console.error(`transform ${name} id:${options.id} invalid`);
          continue;
        }

        const id = row[options.id!];
        const label = row[options.label!];
        const disabled = options.disabled ? !!row[options.disabled] : undefined;

        const item = { id, label, disabled, raw: row };
        if (options.exclude?.(item, row)) {
          continue;
        }

        idLabelList.push(item);
      }

      return idLabelList;
    });

    return response;
  };
}

function axiosDataSource(
  name: string,
  url: string,
  options: {
    id?: string;
    label?: string;
    disabled?: string;
    method?: string;
    body?: PlainObject;
    exclude?: (item: IdLabel, raw: PlainObject) => boolean;
    lazy?: boolean;
    global?: boolean;
  }
) {
  return transformProviderToDataSource(
    axiosTransformProvider(name, url, options),
    {
      ...options,
      logInfo: name
    }
  );
}

type ApiTransformOptions = {
  id?: string;
  label?: string;
  disabled?: string;

  method?: 'post' | 'get';
  body?: PlainObject; // post body

  exclude?: (item: IdLabel, raw: PlainObject) => boolean; // 用于剔除哪些记录

  rawId?: boolean; // 保持id类型不变, 默认会转成number
};

// 注册transform
function registerTransform(name: string, data: Record<number, string>): void; // 静态数据
function registerTransform( // 以url提供数据源
  name: string,
  api: string,
  options?: ApiTransformOptions
): void;
function registerTransform(
  name: string,
  provider: TransformProvider<IdLabelExList>, // 以回调函数提供数据源
  options?: {
    rawId?: boolean;
  }
): void;
function registerTransform(
  name: string,
  datasource: TransformDataSource<IdLabelExList>, // 直接提供datasource
  options?: {
    rawId?: boolean;
  }
): void;

function registerTransform(
  name: string,
  data:
    | Record<number, string>
    | string
    | TransformProvider
    | TransformDataSource,
  options: ApiTransformOptions = {}
) {
  options = {
    method: 'post',
    id: 'id',
    label: 'label',
    ...options
  };

  // name规范: global 以@开头
  const global = name.startsWith('@');

  if (global && isTransformDataSource(data) && !data.global) {
    console.error(`global transform '${name}' can't use non-global datasource`);
    return;
  }

  const old = transforms[name];
  if (old) {
    if (!global && !old.global) {
      // 增加引用计数
      ++old.localRefCount;
      registerUnloadTransform(name);
    }
    if (old.datasource.state.value == 'fail') {
      old.datasource.load(true);
    }
    return;
  }

  // // 保持id类型不变, 默认会转成number
  const rawId = !!options.rawId;
  if (_.isString(data)) {
    // url
    registerApiTransform(
      name,
      axiosDataSource(name, data, { ...options, global, lazy: global }),
      global,
      rawId
    );
  } else if (isTransformDataSource(data)) {
    // datasource
    registerApiTransform(name, data, global, rawId);
  } else if (_.isFunction(data)) {
    // callback function
    registerApiTransform(
      name,
      transformProviderToDataSource(data, { ...options, global, lazy: global }),
      global,
      rawId
    );
  } else {
    // static data
    registerStaticTransform(name, data, global);
  }

  if (!global) {
    const t = transforms[name];
    ++t.localRefCount;
    registerUnloadTransform(name);
  }
}

function registerUnloadTransform(name: string) {
  onUnmounted(() => {
    const t = transforms[name];
    if (t) {
      --t.localRefCount;
      if (t.localRefCount == 0) {
        t.destory();
        delete transforms[name];
      }
    }
  });
}

// 取transform底层kv数据
export function getTransformData(name: string) {
  const t = transforms[name];
  if (!t) {
    console.error(`unknown transform "${name}"`);
    return ref([]);
  }

  // 触发加载
  t.map(0);
  return t.dataRef;
}

// 读取变换的数据值
export function getTransformMap(name: string) {
  const t = transforms[name];
  if (!t) {
    console.error(`unknown transform "${name}"`);
  }

  // 触发加载
  t.map(0);
  return t.dataProps;
}

export function transformToFunction(
  name: string
): (params: ICellRendererParams<any, any>) => string | undefined {
  return (params: ICellRendererParams<any, any>): string | undefined => {
    const t = transforms[name];
    if (!t) {
      console.error(`unknown transform "${name}"`);
      return undefined;
    }
    const map = t.map;

    // 加载成功直接
    if (t.success) {
      return map(params.value);
    }

    const refresher = {
      agApi: params.api,
      colId: params.column!.getColId()
    };

    return map(params.value, refresher);
  };
}

// 取变换值，用于自定义渲染函数
export function transformValue(
  name: string,
  params: ICellRendererParams<any, any> | string | number
): string | undefined {
  if (_.isObject(params)) {
    const fun = transformToFunction(name);
    return fun(params);
  }

  const t = transforms[name];
  if (!t) {
    console.error(`unknown transform2 "${name}"`);
  }

  return t.dataMap[params];
}

export function isTransformRawId(name: string) {
  const t = transforms[name];
  if (!t) {
    return false;
  }

  return t.rawId;
}

// 重新加载transform数据
export function reloadTransform(name: string) {
  const t = transforms[name];
  if (!t) {
    console.error(`unknown transform "${name}"`);
  }

  t.datasource.load(true);
}

// numf 转换缓存
const numfCache: Record<
  number,
  (params: ICellRendererParams<any, string>) => string
> = {};

/**
 * 数额转换
 * @param n 小数位数
 */
export function numf(n = 2) {
  if (numfCache[n]) {
    return numfCache[n];
  }

  const fun = (params: ICellRendererParams<any, string>) =>
    formatDecimal(params.value, n);
  numfCache[n] = fun;
  return fun;
}

// 调整时间格式，用于显示
function fixdatetime(params: ICellRendererParams<any, string>) {
  const v = _.replace(params.value, 'T', ' ');
  const i = v.indexOf('.'); // 有些项目, 时间带毫秒, 去掉
  if (i > 0) {
    return v.substring(0, i);
  }
  return v;
}

function yesno(params: ICellRendererParams<any, string>) {
  return params.value ? '是' : '否';
}

// 整数转换
export const intf = {
  convert: numf(0),
  textAlign: 'right',
  headerTextAlignRight: true,
  width: 110,
  comparator: (v1: any, v2: any) => Number(v1) - Number(v2),
  cellClass: 'format-int-cell'
};

// 2位小数转换
export const decf = {
  convert: numf(),
  textAlign: 'right',
  headerTextAlignRight: true,
  width: 110,
  comparator: (v1: any, v2: any) => Number(v1) - Number(v2),
  cellClass: 'format-dec-cell'
};

// 日期
export const datetimef = {
  convert: fixdatetime,
  textAlign: 'right',
  headerTextAlignRight: true,
  width: 150,
  cellClass: 'format-date-cell'
};

// 人民币金额格式
export const rmb = (decimal = 2) => {
  return (s: string | undefined) => {
    if (!s) {
      return '¥ -';
    }
    return '¥ ' + formatDecimal(s, decimal);
  };
};

export const rmbf = (decimal = 2) => {
  const format = numf(decimal);

  const convert = (params: ICellRendererParams<any, string>) => {
    if (!Number(params.value)) {
      return '¥ -';
    }
    return '¥ ' + format(params);
  };

  return {
    convert,
    textAlign: 'right',
    headerTextAlignRight: true,
    width: 110,
    cellClass: 'format-rmb-cell'
  };
};

// "是/否"
export const yesnof = { convert: yesno };

export { registerTransform };
